<script setup>
import { computed, ref, useAttrs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { useAlert } from 'dashboard/composables';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { useCallsStore } from 'dashboard/stores/calls';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import WhatsAppCallAPI from 'dashboard/api/channel/whatsappCall/whatsappCallAPI';
import whatsAppCallClient from 'dashboard/api/channel/whatsappCall/whatsappCallClient';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  phone: { type: String, default: '' },
  contactId: { type: [String, Number], required: true },
  label: { type: String, default: '' },
  icon: { type: [String, Object, Function], default: '' },
  size: { type: String, default: 'sm' },
  tooltipLabel: { type: String, default: '' },
});

defineOptions({ inheritAttrs: false });
const attrs = useAttrs();
const route = useRoute();
const router = useRouter();
const store = useStore();

const { t } = useI18n();

const dialogRef = ref(null);

const inboxesList = useMapGetter('inboxes/getInboxes');
const contactsUiFlags = useMapGetter('contacts/getUIFlags');

// Twilio Voice inboxes
const voiceInboxes = computed(() =>
  (inboxesList.value || []).filter(
    inbox => inbox.channel_type === INBOX_TYPES.VOICE
  )
);

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');

// WhatsApp Cloud inboxes with calling enabled (gated by account-level feature flag)
const whatsappCallingInboxes = computed(() => {
  if (
    !isFeatureEnabled.value ||
    !isFeatureEnabled.value(
      currentAccountId.value,
      FEATURE_FLAGS.WHATSAPP_CALLING
    )
  ) {
    return [];
  }
  return (inboxesList.value || []).filter(
    inbox =>
      inbox.channel_type === INBOX_TYPES.WHATSAPP &&
      inbox.calling_enabled === true
  );
});

// Combined callable inboxes
const callableInboxes = computed(() => [
  ...voiceInboxes.value,
  ...whatsappCallingInboxes.value,
]);

const hasCallableInboxes = computed(() => callableInboxes.value.length > 0);

// Unified behavior: hide when no phone
const shouldRender = computed(() => hasCallableInboxes.value && !!props.phone);

const isInitiatingCall = computed(() => {
  return contactsUiFlags.value?.isInitiatingCall || false;
});

const isWhatsAppInbox = inbox => {
  return inbox.channel_type === INBOX_TYPES.WHATSAPP;
};

const getInboxIcon = inbox => {
  return isWhatsAppInbox(inbox)
    ? 'i-ri-whatsapp-fill text-green-600'
    : 'i-ri-phone-fill text-n-slate-10';
};

const navigateToConversation = conversationId => {
  const accountId = route.params.accountId;
  if (conversationId && accountId) {
    const path = frontendURL(
      conversationUrl({
        accountId,
        id: conversationId,
      })
    );
    router.push({ path });
  }
};

// Start a WhatsApp Cloud call via WebRTC
const startWhatsAppCall = async inbox => {
  try {
    // 1. Create WebRTC SDP offer
    const sdpOffer = await whatsAppCallClient.createOffer();

    // 2. Send to backend which forwards to Meta Graph API
    const response = await WhatsAppCallAPI.initiateCall(
      inbox.id,
      props.contactId,
      sdpOffer
    );

    const {
      call_id: callId,
      conversation_id: conversationId,
      sdp_answer: sdpAnswer,
    } = response.data;

    // 3. Add to calls store for FloatingCallWidget
    const callsStore = useCallsStore();
    callsStore.addCall({
      callSid: callId,
      conversationId,
      inboxId: inbox.id,
      callDirection: 'outbound',
      provider: 'whatsapp_cloud',
    });

    // 4. Apply SDP answer if returned synchronously (rare for B2C calls)
    if (sdpAnswer) {
      await whatsAppCallClient.setRemoteAnswer(sdpAnswer);
    } else {
      // For B2C calls, Meta sends SDP answer asynchronously via connect webhook.
      // Watch conversation updates for sdp_answer arriving in additional_attributes.
      let stopped = false;
      const numConvId = Number(conversationId);
      const stopWatch = store.watch(
        state => {
          const conv = state.conversations.allConversations.find(
            c => c.display_id === numConvId || c.id === numConvId
          );
          return conv?.additional_attributes?.sdp_answer;
        },
        async newSdp => {
          if (newSdp && !stopped) {
            stopped = true;
            try {
              if (whatsAppCallClient.peerConnection) {
                await whatsAppCallClient.setRemoteAnswer(newSdp);
              }
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error('[WhatsApp Call] Failed to apply SDP answer:', err);
            }
            stopWatch();
          }
        },
        { immediate: true }
      );
      // Auto-cleanup after 60s to prevent memory leak
      setTimeout(() => {
        if (!stopped) {
          stopped = true;
          stopWatch();
        }
      }, 60000);
    }

    // 5. Mark call as active
    callsStore.setCallActive(callId);

    useAlert(t('CONTACT_PANEL.CALL_INITIATED'));
    navigateToConversation(conversationId);
  } catch (error) {
    // Clean up WebRTC on failure
    whatsAppCallClient.endCall();

    const apiError = error?.response?.data?.error || error?.message;
    useAlert(apiError || t('CONTACT_PANEL.CALL_FAILED'));
  }
};

// Start a Twilio Voice call (existing flow)
const startTwilioCall = async inboxId => {
  try {
    const response = await store.dispatch('contacts/initiateCall', {
      contactId: props.contactId,
      inboxId,
    });
    const { call_sid: callSid, conversation_id: conversationId } = response;

    const callsStore = useCallsStore();
    callsStore.addCall({
      callSid,
      conversationId,
      inboxId,
      callDirection: 'outbound',
    });

    useAlert(t('CONTACT_PANEL.CALL_INITIATED'));
    navigateToConversation(response?.conversation_id);
  } catch (error) {
    const apiError = error?.message;
    useAlert(apiError || t('CONTACT_PANEL.CALL_FAILED'));
  }
};

const startCall = async inbox => {
  if (isInitiatingCall.value) return;

  if (isWhatsAppInbox(inbox)) {
    await startWhatsAppCall(inbox);
  } else {
    await startTwilioCall(inbox.id);
  }
};

const onClick = async () => {
  if (callableInboxes.value.length > 1) {
    dialogRef.value?.open();
    return;
  }
  const [inbox] = callableInboxes.value;
  await startCall(inbox);
};

const onPickInbox = async inbox => {
  dialogRef.value?.close();
  await startCall(inbox);
};
</script>

<template>
  <span class="contents">
    <Button
      v-if="shouldRender"
      v-tooltip.top-end="tooltipLabel || null"
      v-bind="attrs"
      :disabled="isInitiatingCall"
      :is-loading="isInitiatingCall"
      :label="label"
      :icon="icon"
      :size="size"
      @click="onClick"
    />

    <Dialog
      v-if="shouldRender && callableInboxes.length > 1"
      ref="dialogRef"
      :title="$t('CONTACT_PANEL.VOICE_INBOX_PICKER.TITLE')"
      show-cancel-button
      :show-confirm-button="false"
      width="md"
    >
      <div class="flex flex-col gap-2">
        <button
          v-for="inbox in callableInboxes"
          :key="inbox.id"
          type="button"
          class="flex items-center justify-between w-full px-4 py-2 text-left rounded-lg hover:bg-n-alpha-2"
          @click="onPickInbox(inbox)"
        >
          <div class="flex items-center gap-2">
            <span :class="getInboxIcon(inbox)" />
            <span class="text-sm text-n-slate-12">{{ inbox.name }}</span>
          </div>
          <span v-if="inbox.phone_number" class="text-xs text-n-slate-10">
            {{ inbox.phone_number }}
          </span>
        </button>
      </div>
    </Dialog>
  </span>
</template>
